import {inject, Injectable} from '@angular/core'
import {HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http'
import {catchError, Observable, Subject, throwError} from 'rxjs'
import {MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition} from '@angular/material/snack-bar'

@Injectable({
    providedIn: 'root'
})
export class HttpService {

    /**
     * Represents the duration for displaying an alert.
     *
     * @type {number}
     * @name alertDuration
     * @description The duration, in seconds, for which an alert should be displayed.
     *              A value of 0 means that the alert must be confirmed to disappear.
     */
    alertDuration = 0;
    horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    verticalPosition: MatSnackBarVerticalPosition = 'bottom';

    private http = inject(HttpClient);
    private snack = inject(MatSnackBar);

    constructor() {
    }

    static buildErrorMessage(errorResponse: any): string {

        let errorMessage = '';
        try {
            errorMessage = errorResponse?.error?.response ?? errorResponse?.error?.message;

            if (!errorMessage)
                errorMessage = errorResponse.statusText;

            if (!errorMessage)
                errorMessage = 'Server is not responsing';
        } catch (e) {
            console.error(e);
            errorMessage = 'Unknown error';
        }
        console.error(errorMessage.toString());
        return errorMessage;
    }

    public get(url: string, params?: HttpParams, httpHeaders?: HttpHeaders, withAlert = false): Observable<any> {
        const headers = httpHeaders ?? this.prepareHeaders();
        return this.http
            .get<any>(url, {headers: headers, params: params})
            .pipe(
                catchError(withAlert ? this.handleError : this.handleErrorNoAlert)
            );
    }

    public post(url: string, data?: any, alert = true): Observable<any> {

        const headers = this.prepareHeaders();
        if (alert) {
            return this.http
                .post<any>(url, this.validateData(data), {headers: headers})
                .pipe(
                    catchError(this.handleError)
                );
        } else {
            return this.http
                .post<any>(url, this.validateData(data), {headers: headers})
                .pipe(
                    catchError(this.handleErrorNoAlert)
                );
        }
    }

    /**
     * Sends a POST request to the specified URL with custom headers and optional data.
     *
     * @param {string} url - The endpoint URL to which the POST request is sent.
     * @param {any} [data] - Optional payload to be included in the request body.
     * @param {boolean} [alert=true] - When true, triggers an error alert handler on failure. Defaults to true.
     * @param {{[key: string]: string}} [customHeaders={}] - An object containing custom headers to include in the request.
     * @return {Observable<any>} An observable representing the HTTP POST response.
     */
    public postCustomHeaders(url: string,
                             data?: any,
                             alert = true,
                             customHeaders: {[key:string]: string} = {}): Observable<any> {

        let headers = this.prepareHeaders();

        Object.keys(customHeaders).forEach(key => {
            headers = headers.append(key, customHeaders[key]);
        });

        if (alert) {
            return this.http
                .post<any>(url, this.validateData(data), {headers: headers})
                .pipe(
                    catchError(this.handleError)
                );
        } else {
            return this.http
                .post<any>(url, this.validateData(data), {headers: headers})
                .pipe(
                    catchError(this.handleErrorNoAlert)
                );
        }
    }

    public put(url: string, data?: any): Observable<any> {
        const headers = this.prepareHeaders();
        return this.http
            .put<any>(url, this.validateData(data), {headers: headers})
            .pipe(
                catchError(this.handleError)
            );
    }

    public delete(url: string, data?: any): Observable<any> {
        const headers = this.prepareHeaders();
        return this.http
            .delete<any>(url, {headers: headers, body: data})
            .pipe(
                catchError(this.handleError));
    }

    /**
     * Post upload method
     * @param url
     * @param formData
     */
    public upload(url: string, formData: FormData): Observable<any> {
        const headers = this.prepareHeaders(HttpRequestType.file);
        return this.http.post<any>(url, formData,
            {headers: headers})
            .pipe(
                catchError(this.handleError));
    }

    public uploadPut(url: string, formData: FormData): Observable<any> {
        const headers = this.prepareHeaders(HttpRequestType.file);
        return this.http.put<any>(url, formData,
            {headers: headers})
            .pipe(
                catchError(this.handleError));
    }

    public uploadWithProgress(url: string, formData: FormData): Subject<number> {
        const headers = this.prepareHeaders(HttpRequestType.file);
        const subject = new Subject<number>();
        this.http.post<any>(url, formData,
            {
                headers: headers,
                reportProgress: true,
                observe: 'events'
            })
            .pipe(
                catchError(this.handleError))
            .subscribe({
                next: res => {
                    if (res.type === HttpEventType.UploadProgress && res.total) {
                        subject.next(Math.round(100 * res.loaded / res.total));
                    } else if (res instanceof HttpResponse) {
                        if (res.body?.success) {
                            subject.complete();
                        } else {
                            subject.error('Some of the files thrown an error');
                        }
                    }
                },
                error: err => subject.error(err)
            });
        return subject;
    }

    public download(url: string, data?: any): Subject<{ progress: number, blob: Blob | null; }> {
        const headers = this.prepareHeaders();
        const subject = new Subject<{ progress: number, blob: Blob | null; }>();
        this.http.post(url, data,
            {
                headers: headers,
                responseType: 'blob',
                reportProgress: true,
                observe: 'events'
            })
            .subscribe({
                next: res => {
                    if (res.type === HttpEventType.DownloadProgress && res.total) {
                        subject.next({progress: Math.round(100 * res.loaded / res.total), blob: null});
                    } else if (res instanceof HttpResponse) {
                        if (res.body) {
                            subject.next({progress: 100, blob: res.body});
                            subject.complete();
                        } else {
                            subject.error('Download failed');
                        }
                    }
                },
                error: err => subject.error(err)
            });
        return subject;
    }

    public getDownload(url: string): Subject<{ progress: number, blob: Blob | null; }> {
        const headers = this.prepareHeaders();
        const subject = new Subject<{ progress: number, blob: Blob | null; }>();
        this.http.get(url,
            {
                headers: headers,
                responseType: 'blob',
                reportProgress: true,
                observe: 'events'
            })
            .subscribe({
                next: res => {
                    if (res.type === HttpEventType.DownloadProgress && res.total) {
                        subject.next({progress: Math.round(100 * res.loaded / res.total), blob: null});
                    } else if (res instanceof HttpResponse) {
                        if (res.body) {
                            subject.next({progress: 100, blob: res.body});
                            subject.complete();
                        } else {
                            subject.error('Download failed');
                        }
                    }
                },
                error: err => {
                    subject.error(err);
                }
            });
        return subject;
    }

    /**
     * Pre-request actions to configure request headers
     * @private
     */
    protected prepareHeaders(type = HttpRequestType.data): HttpHeaders {
        let headers = new HttpHeaders();

        if (type === HttpRequestType.data) {
            headers = headers.append('Content-Type', 'application/json');
        }

        return headers;
    }

    // Error handling
    private handleError = (errorResponse: any) => {
        return throwError(() => {
            const msg = HttpService.buildErrorMessage(errorResponse);
            this.snackError(msg);
            return msg;
        });
    };

    private handleErrorNoAlert = (errorResponse: any) => {
        return throwError(() => {
            return HttpService.buildErrorMessage(errorResponse);
        });
    };

    private snackError(message: string): void {
        this.snack.open(
            message,
            'OK',
            {
                duration: this.alertDuration,
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                panelClass: 'shared-snackbar-error'
            });
    }

    /**
     * Check data for unwanted and invalid fragments
     * - trim() string values
     * @param data
     * @private
     */
    private validateData(data?: any): any {
        if (!data) return data;

        const valData = JSON.parse(JSON.stringify(data));

        if (typeof (valData) === 'string') {
            return (<string>valData).trim();
        }

        if (typeof (valData) === 'object') {
            Object.keys(valData).forEach(key => {
                if (typeof (valData[key]) === 'string') {
                    valData[key] = (<string>valData[key]).trim();
                }
            });
            return valData;
        }

        return data;
    }
}

export enum HttpRequestType {
    data,
    file
}
